import ComponentSelector from "./ComponentSelector";
import CurrentLayerSelector from "./CurrentLayerSelector";
import InflationSelector from "./InflationSelector";
import Notes from "./Notes";
import OldSummaryTable from "./OldSummaryTable";
import PriorLayerSelector from "./PriorLayerSelector";
import * as support from "./RateChange.support";
import RebaseSelector from "./RebaseSelector";
import SideBySideClaimsChart from "./SideBySideClaimsChart";
import SummaryTable from "./SummaryTable";
import WaterfallChart from "./WaterfallChart";
import YearOnYearExposures from "./YearOnYearExposures";
import {
  Alert,
  AlertTitle,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  Link,
  Stack,
} from "@mui/material";
import * as logger from "common/logger";
import Component from "components/Component";
import Button from "components/common/Button";
import Disabled from "components/common/Disabled";
import TextNote from "components/common/TextNote";
import Visible from "components/common/Visible";
import ClaimsDeltaChart from "components/inputs/claims/ClaimsDeltaChart";
import * as config from "config";
import * as labels from "config.labels";
import { useState } from "react";
import { connect, useDispatch, useSelector } from "react-redux";
import * as analyticsService from "services/analyticsService";
import * as staticDataService from "services/staticDataService";
import * as staticDataHooks from "store/hooks/staticDataHooks";
import * as rateChangeSlice from "store/rateChange";
import * as submissionSelectors from "store/selectors/submission/submissionSelector";

const _useRateChangeComponents = ({ activeLayerRateChange }) => {
  const componentDisabled = activeLayerRateChange.componentDisabled ?? {};

  const inflation = activeLayerRateChange.inflation ?? 0;
  const exposureChanges = support.useActiveLayerExposureChanges();

  // The prior/current exposure inputs behind the exposure step, passed to the engine so they are
  // echoed back on the result's `details` and shown in the waterfall tooltip.
  const totalOverride = activeLayerRateChange?.exposureOverrides?.["TOTAL"];
  const exposureDetails = {
    metrics: (exposureChanges.components ?? [])
      .filter((component) => component.selected)
      .map((component) => ({
        name: component.exposure?.name ?? component.key,
        prior: component.priorValue,
        current: component.currentValue,
        deltaPct: component.deltaPct,
      })),
    totalOverridePct:
      totalOverride != null && totalOverride !== ""
        ? Number(totalOverride)
        : null,
  };

  const componentDefns = [
    {
      key: "structure",
      name: "Δ Structure",
      metric: { type: "Structure" },
    },
    {
      key: "commission",
      name: "Δ Commission",
      metric: { type: "Commission" },
    },
    {
      key: "exposure",
      name: "Δ Exposure",
      metric: {
        type: "Manual",
        deltaFraction: exposureChanges.totalRatio - 1,
        details: exposureDetails,
      },
    },
    {
      key: "uwJudgment",
      name: "Δ Judgment",
      metric: { type: "UWJudgment" },
    },
    {
      key: "inflation",
      name: "Δ Inflation",
      metric: {
        type: "Manual",
        deltaFraction: 0.01 * inflation,
      },
    },
    {
      key: "pure",
      name: "Δ Pure",
      metric: { type: "Residual" },
    },
  ];

  const components = componentDefns
    .filter((x) => !componentDisabled?.[x.key])
    .flatMap((x) =>
      activeLayerRateChange?.rebaseAfter?.[x.key]
        ? [
            x,
            {
              key: "rebase",
              name: "Model",
              metric: {
                type: "Rebase",
              },
            },
          ]
        : [x]
    );

  return components;
};

const useRateChangeTrigger = ({ activeLayerRateChange, engine }) => {
  const {
    data: config,
    isLoading: isConfigLoading,
    isError: isConfigError,
  } = staticDataService.useConfigQuery("analytics");

  const [
    trigger,
    { currentData, isFetching, isError },
  ] = analyticsService.useLazyRateChangeAnalyticsQuery();

  const [hasErrorResult, setHasErrorResult] = useState(false);

  const submission = useSelector(
    submissionSelectors.selectResolvedSubmissionWithoutInput
  );

  const components = _useRateChangeComponents({ activeLayerRateChange });

  const dispatch = useDispatch();

  const calculate = () => {
    const engine = config?.rateChange?.engine;
    if (!engine) {
      logger.error("Skipping Rate Change calculation. No engine configured.");
      return false;
    }
    const queryData = {
      submission,
      engine: config?.rateChange?.engine,
      args: { components },
    };
    dispatch(
      rateChangeSlice.updateLayerProperties({
        chartData: null,
      })
    );
    setHasErrorResult(false);
    trigger(queryData)
      .unwrap()
      .then((responseData) => {
        if (responseData?.values) {
          dispatch(
            rateChangeSlice.updateLayerProperties({
              chartData: responseData.values,
            })
          );
        } else {
          setHasErrorResult(true);
        }
      })
      .catch(() => {});
    return true;
  };

  return {
    calculate,
    currentData,
    isFetching: isConfigLoading || isFetching,
    isError: isConfigError || isError || hasErrorResult,
  };
};

const MissingConfigDialog = ({ open, onClose }) => (
  <Dialog open={open} onClose={onClose} maxWidth={"xs"}>
    <DialogTitle>{"Missing Config"}</DialogTitle>
    <DialogContent>
      {
        "No rate change calculations have been configured in this environment, contact "
      }
      <Link href={`mailto:${config.SUPPORT_EMAIL}`}>
        {config.SUPPORT_EMAIL}
      </Link>
      {" for assistance."}
    </DialogContent>
    <DialogActions>
      <Button onClick={onClose}>{"OK"}</Button>
    </DialogActions>
  </Dialog>
);

const MigrateToDefaults = () => {
  const translateLabel = staticDataHooks.useTranslator("uiLabels");

  const dispatch = useDispatch();

  const rateChange = useSelector(rateChangeSlice.selectRateChangeInput);
  const { data: submissionDefaults } = staticDataService.useConfigQuery(
    "submission_defaults"
  );
  const { data: generalConfiguration } = staticDataService.useConfigQuery(
    "general_configuration"
  );

  if (
    support.isUsingLatestDefaults({
      rateChange,
      defaults: submissionDefaults?.rateChange,
      config: generalConfiguration?.rateChangeMigration,
    })
  ) {
    return <></>;
  }

  const migrate = () =>
    dispatch(
      rateChangeSlice.setRateChangeState(
        support.migrateToLatestDefaults({
          rateChange,
          defaults: submissionDefaults?.rateChange,
        })
      )
    );

  return (
    <Alert
      severity={"info"}
      sx={{
        width: "100%",
      }}
    >
      <AlertTitle>
        {translateLabel(labels.RATE_CHANGE_MIGRATE_TO_DEFAULTS_TITLE, {
          default: "Migrate to New Methodology",
        })}
      </AlertTitle>
      <Stack direction={"column"} gap={1}>
        {translateLabel(labels.RATE_CHANGE_MIGRATE_TO_DEFAULTS_DESCRIPTION, {
          default:
            "The rate change configured for some layers is not following the new default methodology. Use the button below to migrate it to the latest methodology. (Note that you will need to re-calculate the rate change for all layers.)",
        })}
        <Button onClick={migrate}>
          {translateLabel(labels.RATE_CHANGE_MIGRATE_TO_DEFAULTS_BUTTON, {
            default: "Migrate",
          })}
        </Button>
      </Stack>
    </Alert>
  );
};

const RateChange = ({ rateChange, activeLayerRateChange }) => {
  const translateLabel = staticDataHooks.useTranslator("uiLabels");

  const {
    calculate: triggerCalculate,
    currentData,
    isFetching,
    isError,
  } = useRateChangeTrigger({
    activeLayerRateChange,
  });

  const [isMissingConfigDialogOpen, setIsMissingConfigDialogOpen] = useState(
    false
  );

  const calculate = () => {
    if (!triggerCalculate()) {
      setIsMissingConfigDialogOpen(true);
    }
  };

  const [claimsComparisonQuery, setClaimsComparisonQuery] = useState({
    primaryMatch: "_DATE_OF_LOSS",
    requiredMatchColumns: ["_COVERAGE", "_CLAIMANT_NAME"],
    matchColumns: ["_DESCRIPTION", "_OPEN_CLOSED"],
    textColumns: [
      "_COVERAGE",
      "_CLAIMANT_NAME",
      "_DESCRIPTION",
      "_OPEN_CLOSED",
    ],
    numericColumns: ["_TOTAL_LOSS"],
  });

  return (
    <Grid item xs={12}>
      <MissingConfigDialog
        open={isMissingConfigDialogOpen}
        onClose={() => setIsMissingConfigDialogOpen(false)}
      />
      <Stack direction={"column"} spacing={config.GRID_SPACING}>
        <Visible
          byTag={"<RateChange>.migrateToDefaults"}
          defaultVisible={false}
        >
          <MigrateToDefaults />
        </Visible>
        <Stack
          direction={{ xs: "column", md: "row" }}
          spacing={config.GRID_SPACING}
        >
          <CurrentLayerSelector />
          <PriorLayerSelector />
        </Stack>
        {activeLayerRateChange?.layerId &&
          activeLayerRateChange?.selectedPriorLayerId && (
            <>
              <Visible
                byTag={"<RateChange>.summaryTable"}
                defaultVisible={false}
              >
                <SummaryTable />
              </Visible>
              <Visible
                byTag={"<RateChange>.justification"}
                defaultVisible={false}
              >
                <TextNote
                  title={translateLabel(
                    labels.RATE_CHANGE_JUSTIFICATION_TITLE,
                    {
                      default: "Rate Change Justification",
                    }
                  )}
                  noteKey={`pricing.rateChange.${activeLayerRateChange?.layerId}.justification`}
                  hasEditButton={true}
                />
              </Visible>
              <Visible byTag={"<RateChange>.components"} defaultVisible={true}>
                <ComponentSelector />
              </Visible>
              {isError || currentData?.error ? (
                <Component
                  title={"Error"}
                  options={
                    <Disabled ifReadOnly>
                      <Button onClick={calculate} isLoading={isFetching}>
                        {"Calculate"}
                      </Button>
                    </Disabled>
                  }
                >
                  <Alert severity={"error"}>
                    {currentData?.failure ??
                      currentData?.error?.message ??
                      `Error calculating rate change. Please contact ${config.SUPPORT_EMAIL}.`}
                  </Alert>
                </Component>
              ) : (
                <>
                  <Visible
                    byTag={"<RateChange>.oldSummaryTable"}
                    defaultVisible={false}
                  >
                    <OldSummaryTable isLoading={isFetching} />
                  </Visible>

                  <WaterfallChart
                    activeLayerRateChange={activeLayerRateChange}
                    calculate={calculate}
                    isLoading={isFetching}
                  />
                </>
              )}
              <Visible
                byTag={"<RateChange>.yearOnYearExposures"}
                defaultVisible={true}
              >
                <YearOnYearExposures />
              </Visible>
              <Visible byTag={"<RateChange>.inflation"} defaultVisible={true}>
                <InflationSelector />
              </Visible>
              <Visible byTag={"<RateChange>.rebase"} defaultVisible={true}>
                <RebaseSelector />
              </Visible>
              <Visible byTag={"<RateChange>.notes"} defaultVisible={true}>
                <Notes key={rateChange?.activeLayerId ?? null} />
              </Visible>
            </>
          )}
        <Visible byTag={"<RateChange>.sideBySideClaimsChart"} defaultVisible>
          <SideBySideClaimsChart />
        </Visible>
        <Visible byTag={"<RateChange>.claimsDeltaChart"}>
          <ClaimsDeltaChart
            query={claimsComparisonQuery}
            setQuery={setClaimsComparisonQuery}
          />
        </Visible>
      </Stack>
    </Grid>
  );
};

const mapStateToProps = (state) => ({
  rateChange: rateChangeSlice.selectRateChange(state),
  activeLayerRateChange: rateChangeSlice.selectActiveLayerRateChange(state),
});

export default connect(mapStateToProps)(RateChange);
